import { supabase } from './supabaseClient';

async function callAdminUsers<T = Record<string, unknown>>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ action, ...body }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? `Request failed (${res.status})`);
  return json as T;
}

export interface AdminUserRow {
  user_id: string;
  role: string;
  full_name: string | null;
  email: string;
  last_sign_in: string | null;
  confirmed: boolean;
  created_at: string;
  invited_at: string | null;
  avatar_url: string | null;
}

export const adminApi = {
  listUsers: () => callAdminUsers<{ users: AdminUserRow[] }>('list'),
  inviteUser: (email: string, role: string, full_name: string) =>
    callAdminUsers<{ success: true; user_id: string }>('invite', { email, role, full_name }),
  createUser: (email: string, password: string, role: string, full_name: string) =>
    callAdminUsers<{ success: true; user_id: string }>('create_user', { email, password, role, full_name }),
  updateRole: (user_id: string, role: string) => callAdminUsers('update_role', { user_id, role }),
  updateUser: (user_id: string, fields: { email?: string; full_name?: string; password?: string }) =>
    callAdminUsers('update_user', { user_id, ...fields }),
  removeUser: (user_id: string) => callAdminUsers('remove', { user_id }),
  listAuditLogs: () => callAdminUsers<{ logs: Record<string, unknown>[] }>('list_audit_logs'),
  deleteAuditLogs: (master_password: string, opts: { log_ids?: string[]; delete_all?: boolean }) =>
    callAdminUsers('delete_audit_logs', {
      master_password,
      ...(opts.delete_all ? { delete_all: 'true' } : { log_ids: JSON.stringify(opts.log_ids ?? []) }),
    }),
};

export interface CustomRoleRow {
  id: string;
  key: string;
  label: string;
  tone: string;
  sort_order: number;
  is_system: boolean;
}

// Plain table reads/writes (not edge-function calls) — RLS already enforces
// read-for-everyone / write-for-founder-only on custom_roles directly.
export const rolesApi = {
  list: async (): Promise<CustomRoleRow[]> => {
    const { data, error } = await supabase.from('custom_roles').select('*').order('sort_order');
    if (error) throw error;
    return (data as CustomRoleRow[]) ?? [];
  },
  create: async (key: string, label: string, tone: string): Promise<void> => {
    const { error } = await supabase.from('custom_roles').insert({ key, label, tone, sort_order: 100, is_system: false });
    if (error) throw error;
  },
  remove: async (key: string): Promise<void> => {
    const { error } = await supabase.from('custom_roles').delete().eq('key', key);
    if (error) throw error;
  },
};

export interface RolePermissionRow {
  role: string;
  page_key: string;
}

// Plain table reads/writes for the rxk-specific page-access matrix — RLS
// enforces read-for-everyone / write-for-admin-or-founder.
export const rolePermissionsApi = {
  list: async (): Promise<RolePermissionRow[]> => {
    const { data, error } = await supabase.from('rxk_role_permissions').select('role, page_key');
    if (error) throw error;
    return (data as RolePermissionRow[]) ?? [];
  },
  grant: async (role: string, page_key: string): Promise<void> => {
    const { error } = await supabase.from('rxk_role_permissions').insert({ role, page_key });
    if (error) throw error;
  },
  revoke: async (role: string, page_key: string): Promise<void> => {
    const { error } = await supabase.from('rxk_role_permissions').delete().eq('role', role).eq('page_key', page_key);
    if (error) throw error;
  },
};
